import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  MenuItem,
  FormControl,
  Select,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import style from "./style";
import { paymentTermsOptions } from "../../../../../constants/data";
import { getDueDateFromPaymentTerm } from "../../../../../utils/Utilities";
import color from "../../../../../constants/color";

const CreateBillPage = ({
  itemList,
  handleAddNewBill,
  loading,
  supplierList,
}) => {
  const [formData, setFormData] = useState({
    supplier: "",
    billDate: dayjs(),
    dueDate: dayjs(),
    billNo: "",
    orderNo: "",
    paymentTerms: "",
    productInformation: [
      {
        productId: "",
        name: "",
        description: "",
        unitCost: 0,
        quantity: 0,
        quoteQuantity: 0,
        amount: 0,
      },
    ],
    discountPercentage: 0,
    taxPercentage: 0,
    total: 0,
    discountValue: 0,
    taxValue: 0,
    grandTotal: 0,
  });

  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.productInformation];

    // Update the specific field
    updatedProducts[index][field] = value;

    // Calculate amount if quantity or unitCost changes
    if (field === "quantity" || field === "quoteQuantity" || field === "unitCost") {
      const quantity =
        field === "quantity"
          ? Number(value)
          : Number(updatedProducts[index].quoteQuantity);
      const unitCost =
        field === "unitCost"
          ? Number(value)
          : Number(updatedProducts[index].unitCost);
      updatedProducts[index].amount = quantity * unitCost;
    }

    if (field === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedProducts[index].productId = selectedItem._id || "";
        updatedProducts[index].unitCost = Number(selectedItem.purchasePrice || 0);
        updatedProducts[index].quantity = Number(selectedItem.quantity || 1);
        updatedProducts[index].description = selectedItem.description || "";
        updatedProducts[index].amount = "";
        updatedProducts[index].quoteQuantity = "";
      }
    }

    setFormData((prev) => ({
      ...prev,
      productInformation: updatedProducts,
    }));

    calculateTotals(updatedProducts);
  };

  const calculateTotals = (products) => {
    const total = products.reduce((sum, item) => sum + (item.amount || 0), 0);
    const discountValue = total * (formData.discountPercentage / 100);
    const taxableAmount = total - discountValue;
    const taxValue = taxableAmount * (formData.taxPercentage / 100);
    const grandTotal = taxableAmount + taxValue;

    setFormData((prev) => ({
      ...prev,
      total,
      discountValue,
      taxValue,
      grandTotal,
    }));
  };

  const handlePercentageChange = (field, value) => {
    const total = formData.productInformation.reduce((sum, item) => sum + (item.amount || 0), 0);

    const numValue = Number(value) || 0;
    let discountPercentage = formData.discountPercentage;
    let taxPercentage = formData.taxPercentage;
    let discountValue = formData.discountValue;
    let taxValue = formData.taxValue;
    let grandTotal = formData.grandTotal;

    if (field === "discountPercentage") {
      discountPercentage = numValue;
      discountValue = total * (discountPercentage / 100);
      const taxableAmount = total - discountValue;
      taxValue = taxableAmount * (taxPercentage / 100);
      grandTotal = total - discountValue + taxValue;
    }

    if (field === "taxPercentage") {
      discountValue = total * (discountPercentage / 100);
      taxPercentage = numValue;
      const taxableAmount = total - discountValue;
      taxValue = taxableAmount * (taxPercentage / 100);
      grandTotal = total - discountValue + taxValue;
    }

    setFormData((prev) => ({
      ...prev,
      total,
      discountPercentage,
      discountValue,
      taxPercentage,
      taxValue,
      grandTotal,
    }));
  };

  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      productInformation: [
        ...prev.productInformation,
        {
          productId: "",
          name: "",
          description: "",
          unitCost: 0,
          quantity: 0,
          quoteQuantity: 0,
          amount: 0,
        },
      ],
    }));
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...formData.productInformation];
    updatedProducts.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      productInformation: updatedProducts,
    }));

    calculateTotals(updatedProducts);
  };

  const handleSupplierSelect = (supplier) => {
    setFormData((prev) => ({
      ...prev,
      supplier: supplier._id,
    }));
  };

  const isFormValid = () => {
    if (
      !formData.supplier ||
      !formData.billNo ||
      !formData.orderNo ||
      !formData.paymentTerms
    ) {
      return false;
    }

    // Check all products
    for (let product of formData.productInformation) {
      if (!product.description || !product.unitCost || !product.quantity) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const cleanedProducts = formData.productInformation.map((product) => ({
      productId: product.productId,
      name: product.name,
      description: product.description,
      unitCost: Number(product.unitCost),
      quantity: Number(product.quoteQuantity),
      amount: Number(product.amount),
    }));

    const payload = {
      supplier: formData.supplier,
      billDate: formData.billDate.toISOString(),
      dueDate: formData.dueDate.toISOString(),
      billNo: formData.billNo,
      orderNo: formData.orderNo,
      paymentTerms: formData.paymentTerms,
      productInformation: cleanedProducts,
      total: formData.total,
      discountPercentage: formData.discountPercentage,
      discountValue: formData.discountValue,
      taxPercentage: formData.taxPercentage,
      taxValue: formData.taxValue,
      grandTotal: formData.grandTotal,
    };

    handleAddNewBill(payload);
  };


  const handlePaymentTermSelection = (value) => {
    handleInputChange("paymentTerms", value);
    const dueDate = getDueDateFromPaymentTerm(value, formData.billDate);
    if (dueDate?.isValid()) {
      handleInputChange("dueDate", dueDate.endOf("day"));
    }
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Create Bill</Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container sx={style.mainContainer}>
          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Supplier</Typography>
                <Autocomplete
                  id="autocomplete"
                  options={[
                    { _id: "add_new", name: "+ New Supplier" },
                    ...(supplierList || []),
                  ]}
                  getOptionLabel={(option) => option?.name}
                  onChange={(_, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewSupplier"); // redirect page
                      return;
                    }
                    if (newValue) handleSupplierSelect(newValue);
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <>
                      <TextField
                        placeholder="Supplier"
                        {...params}
                        sx={{ ...style.modalInput }}
                        variant="outlined"
                      />
                    </>
                  )}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={{ ...style.inputText }}>Bill Number</Typography>
                <TextField
                  fullWidth
                  placeholder={"Bill Number"}
                  value={formData.billNo}
                  onChange={(e) => handleInputChange("billNo", e.target.value)}
                  sx={style.modalInput}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={{ ...style.inputText }}>Order Number</Typography>
                <TextField
                  fullWidth
                  placeholder={"Order Number"}
                  required
                  onChange={(e) => handleInputChange("orderNo", e.target.value)}
                  sx={style.modalInput}
                />
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={{ ...style.inputText, marginTop: '24px' }}>Bill Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.billDate}
                    onChange={(newValue) =>
                      handleInputChange("billDate", newValue)
                    }
                    sx={{
                      ...style.modalInput,
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        paddingRight: "23px !important",
                      },
                    }}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={{ ...style.inputText, marginTop: '24px' }}>Due Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.dueDate}
                    onChange={(newValue) =>
                      handleInputChange("dueDate", newValue)
                    }
                    sx={{
                      ...style.modalInput,
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        paddingRight: "23px !important",
                      },
                    }}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                    minDate={formData.billDate}
                    disabled={formData.paymentTerms !== "Custom"}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={style.inputBox}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "2.7em",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Typography sx={{ ...style.inputPaymentTerm }}>
                    Payment Terms<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Select
                    value={formData.paymentTerms}
                    onChange={(e) => {
                      handlePaymentTermSelection(e.target.value);
                    }}
                    fullWidth
                    displayEmpty
                    renderValue={(selected) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Select Payment Terms
                        </span>
                      )
                    }
                  >
                    {paymentTermsOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Grid>
          <Box sx={style.itemSection}>
            <Box
              sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
            ></Box>
            <Typography sx={style.gridText}>Product & Services</Typography>
          </Box>
          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            {formData.productInformation.map((product, index) => {
              const stockQuantity = product.quantity;
              return (
                <Box sx={style.leaveSection} key={index}>
                  <Grid container sx={style.itemGrid}>
                    <Grid item xs={12} lg={1.8}>
                      <Autocomplete
                        id="autocomplete"
                        options={[
                          { _id: "add_new", title: "+ New Product" },
                          ...(itemList || []),
                        ]}
                        getOptionLabel={(option) => option?.title}
                        onChange={(event, newValue) => {
                          if (newValue?._id === "add_new") {
                            navigate("/dashboard/addNewItem");
                            return;
                          }
                          if (!newValue) {
                            handleProductChange(index, "name", "");
                            handleProductChange(index, "quoteQuantity", "");
                            return;
                          }
                          handleProductChange(index, "name", newValue?.title || "");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={
                              option._id === "add_new"
                                ? { color: color.secondary }
                                : {}
                            }
                          >
                            {option.title}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder="Product"
                            {...params}
                            sx={style.modalInput}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3.1}>
                      <TextField
                        required
                        fullWidth
                        placeholder="Description"
                        value={product.description}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        sx={style.modalInput}
                      />
                    </Grid>
                    <Grid item xs={12} lg={2}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ ...style.customInput, width: "100%" }}
                      >
                        <TextField
                          value={product.quoteQuantity}
                          type="number"
                          placeholder="Quantity"
                          sx={{ ...style.quality }}
                          inputProps={{ min: 1, step: 1 }}
                          onChange={(e) => {
                            const rowValue = e.target.value;
                            const value = parseInt(rowValue, 10);
                            const maxQuantity = stockQuantity;
                            // if (isNaN(value)) return
                            if (
                              !isNaN(value) &&
                              value >= 0 &&
                              value <= maxQuantity
                            ) {
                              handleProductChange(index, "quoteQuantity", value);
                            } else if (e.target.value === "") {
                              handleProductChange(index, "quoteQuantity", "");
                            } else {
                              const validValue = value < 0 ? 0 : maxQuantity;
                              handleProductChange(
                                index,
                                "quoteQuantity",
                                validValue
                              );
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" variant="filled">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#DEDEDE",
                                    height: 41,
                                    p: 0,
                                    ...style.inputBgBox,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 35,
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "end",
                                      fontWeight: "600",
                                      userSelect: "none",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    {stockQuantity}
                                  </Box>
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <Box
                                        sx={{
                                          bgcolor: "white",
                                          p: 1,
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            color: "#373D3F",
                                            mb: 1,
                                          }}
                                        >
                                          Stock Details
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            color: "#373D3F",
                                            fontWeight: 400,
                                          }}
                                        >
                                          Stock on Hand :{" "}
                                          {stockQuantity || "0"}
                                        </Typography>
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "white !important",
                                          p: 0,
                                          border:
                                            "1px solid #878787 !important",
                                          boxShadow: "none !important",
                                          "& .MuiTooltip-arrow": {
                                            color: "white !important",
                                            "&::before": {
                                              border: "1px solid #878787",
                                              backgroundColor:
                                                "white !important",
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      sx={{
                                        color: "#878787",
                                      }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* </Grid> */}
                    <Grid item xs={9} lg={1.8}>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Rate"
                        value={
                          product.unitCost === ""
                            ? ""
                            : Number(product.unitCost)
                        }
                        onChange={(e) =>
                          handleProductChange(e.target.value, index, "unitCost")
                        }
                        sx={{ ...style.rateField }}
                      />
                    </Grid>

                    <Grid item xs={9} lg={1.8}>
                      <TextField
                        required
                        fullWidth
                        disabled
                        type="number"
                        placeholder="Amount"
                        value={product.amount}
                        sx={style.modalInput}
                        onChange={(e) =>
                          handleProductChange(index, "amount", e.target.value)
                        }
                      />
                    </Grid>
                    {index === 0 && (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box sx={style.addIcon} onClick={handleAddProduct}>
                          +
                        </Box>
                      </Grid>
                    )}
                    {index !== 0 && (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box
                          sx={style.deleteIcon}
                          onClick={() => handleRemoveProduct(index)}
                        >
                          <DeleteOutlineIcon />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={style.container4}>
                <Box
                  sx={{
                    height: "140px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#202020",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      "&::before": {
                        content: '""',
                        width: "3px",
                        height: "16px",
                        backgroundColor: "#28AA4A",
                        marginRight: "8px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Discount and Tax (%)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Discount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Discount"
                          value={formData.discountPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "discountPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Tax
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Tax"
                          value={formData.taxPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "taxPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={style.container3}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "140px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.total.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Tax ({formData.taxPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.taxValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Discount ({formData.discountPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.discountValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      borderTop: "1px solid #eee",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                      {formData.grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              color="success"
              sx={style.cancelButtons}
            >
              Cancel
            </Button>
          </Box>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              type="submit"
              color="success"
              sx={style.jobButtons}
              disabled={!isFormValid()}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};
export default CreateBillPage;
